import { Scadenza } from '@scadenze/model/scadenze.models';
import { ForfettappApiClientOptions } from '@shared/forfettapp-api-client';
import { ServiceLocator } from '@shared/service.locator';
import { TipoEmissioneUnico } from 'app/main/emissioni-folder/emissioni-models';
import { forkJoin } from 'rxjs';
import { ForfettarioService } from '../forfettario.service';
import { FamiliariACarico } from './visualizza-familiari-a-carico/visualizza-familiari-a-carico.models';
import { RedditiDaTerreni } from './visualizza-quadro-ra/visualizza-quadro-ra.models';
import { RedditiDaFabbricati } from './visualizza-quadro-rb/visualizza-quadro-rb.models';
import { RedditiDaDipendente } from './visualizza-quadro-rc/visualizza-quadro-rc.models';
import { RedditiTassazioneSeparata } from './visualizza-quadro-rm/visualizza-quadro-rm.models';
import { QuadroRnData } from './visualizza-quadro-rn/visualizza-quadro-rn.models';
import { OneriESpese } from './visualizza-quadro-rp/visualizza-quadro-rp.models';
import { AddizionaliComunaleRegionale } from './visualizza-quadro-rv/visualizza-quadro-rv.models';
import { RedditiFinanziariEsteri } from './visualizza-quadro-rw/visualizza-quadro-rw.models';

export interface QuadroDichiarazione {
    canEdit: boolean;
    canAddNew: boolean;
    toBeValidated: boolean;
    backofficeModified: boolean;
}

export interface DettagliContributiVersatiModel {
    primoAccontoContributivoAnnoPrecedente: number | null;
    secondoAccontoContributivoAnnoPrecedente: number | null;
    totaleRateFisseContributivoAnnoPrecedente: number | null;
    saldoContribuzioneDaVersareContributivoAnnoPrecedente: number | null;
    contributiCompensati: number;
}

export interface NotificaRicalcoloSecondoAccontoPayload {
    anno: number;
    nome: string;
}

export interface NotificaRicalcoloSecondoAccontoSceltaGestione {
    modalitaPagamentoSecondoAcconto: ModalitaPagamentoSecondoAcconto;
    metodoStorico: boolean;
    incassiPrevistiNovembre: number | null;
    incassiPrevistiDicembre: number | null;
}

export enum ModalitaPagamentoSecondoAcconto {
    ContributiNovembreTasseGennaio,
    ContributiAndTasseNovembre,
}

export interface DettagliAccontiVersatiModel {
    primoAccontoVersato: number;
    secondoAccontoVersato: number;
    altriAccontiVersati: number;
}

export interface DichiarazioneModel {
    eccedenzaImposte: number;
    aliquotaImpostaSostitutiva: number;
    anno: number;
    codiceFiscaleContribuente: string;
    contribuzione: ContribuzioneModel;
    contribuzioneUtenteId: number;
    contributiVersati: number;
    contributiCompensati: number;
    contributiDaVersare: number;
    creditoResiduo: number;
    creditoPrecedenteCompensato: number;
    dichiarazioneAnnoPrecedente: DichiarazioneModel;
    id: number;
    inviiId: number;
    dataInvioUnico?: Date | string;
    protocolloInvioUnico: string;
    limiteRicavi: number;
    numeroRateUnico: number;
    percentualePrimoAcconto: number;
    percentualeSecondoAcconto: number;
    ritenuteCertificate: number;
    saldoImpostaCompensata: number;
    stato: StatoDichiarazione;
    statoDescrizione: string;
    utenteId: number;
    impostaCalcolata: number;
    impostaNetta: number;
    primoAcconto: number;
    primoAccontoVersato: number;
    reddito: number;
    redditoImponibile: number;
    ricavi: number;
    ritenuteSubite: number;
    saldoImposte: number;
    secondoAcconto: number;
    secondoAccontoVersato: number;
    tipoEmissioneUnico: TipoEmissioneUnico;
    emissioneOperatoreId?: number;
    contribuente: DichiarazioneContribuente;
    altriAccontiVersati: number;
    saldoGiugno: number;
    dataConfermaUnico: string;
    dataCreazioneScadenzeUnico: string;
    dataCreazioneScadenzeSecondoAcconto: string;
    emissioneCompletataConSuccesso?: boolean;
    emissioniId: number;
    secondoAccontoRicalcolato: boolean;
    importazioniStoricoDaEntiId?: number;
    statoValidazioneUnico?: any; // DA RIVEDERE
    tipoAgenteEnasarco?: TipoAgenteEnasarco;
    percentualeEnasarco?: number;
    altriRedditi?: number;
}

export interface DichiarazioneContribuente {
    partitaIva: string;
    nome: string;
    cognome: string;
    dataInizioIva: string;
    dataFineIva: string | null;
}

export interface ContribuzioneModel {
    accontiVersati: number;
    aliquotaContributiPrevidenziali: number;
    anno: number;
    codiceAziendaInps: string;
    contributiCompensati: number;
    contributiDaVersare: number;
    contributiDovuti: number;
    contributiVersati: number;
    credito: number;
    dataCancellazioneCassa: string;
    dataIscrizioneCassa: string;
    escludibileInFattura: boolean;
    minimaleReddito: number;
    minimaleRedditoAnnoSuccessivo: number;
    parametriAnnoSuccessivoDefinitivi: boolean;
    percentualeContributiOltreMinimale: number;
    percentualeContributiOltreMinimaleAnnoSuccessivo: number;
    percentualePrimoAcconto: number;
    percentualeSecondoAcconto: number;
    primoAcconto: number;
    redditoEccedenteIlMinimale: number;
    saldoDaVersare: number;
    secondoAcconto: number;
    sedeInps: string;
    tipo: TipoContribuzione;
    totaleGiugno: number;
    totaleNovembre: number;
    utentiId: number;
    esonero: boolean;
    primoAccontoPerAnnoSuccessivo: number;
    quotaEccedenteIlMinimaleAnnoSuccessivo: number;
    redditoEccedenteIlMinimaleAnnoSuccessivo: number;
    saldoContributi: number;
    secondoAccontoPerAnnoSuccessivo: number;
    versamentiAltreContribuzioni: number;
}

export interface ParametriGeneraliModel {
    anno: number;
    percImposte: number;
    percImposteRidotte: number;
    percPrimoAcconto: number;
    percSecondoAcconto: number;
    dataPagUnico: Date;
    descPagUnico: string;
    descPagPrimoAcconto: string;
    dataPagSecondoAcconto: Date;
    descPagSecondoAcconto: string;
    dataPagBolli: Date;
    descPagBolli: string;
    limiteCostoAttrezzature: number;
    limiteCostoDipendenti: number;
    descVersamentoUnico: string;
    descAccontoNovembre: string;
    descSoluzioneUnicaAcconti: string;
    minimoImpostaPagamentoAcconti: number;
    minimoImpostaDivisioneAcconti: number;
    rateMinimaleInteresse: number;
    rateNumeroMassimo: number;
    rateTassoInteresseAnnuo: number;
    maggiorazionePagamentoPosticipato: number;
    codiciTributiSaldo: string;
    codiciTributiPrimoAcconto: string;
    codiciTributiSecondoAcconto: string;
    dataLimiteInvioUnico: Date;
}

export interface ParametriGeneraliRateModel {
    parametriGeneraliAnno: number;
    numeroRata?: number;
    rataMaggiorata: boolean;
    dataPagamento: Date;
    percentualeInteressi: number;
}

export interface VersamentiAltreContribuzioniModel {
    id: number;
    dataPagamento: string;
    annoCompetenza: number;
    importoSoggettivo: number;
    importoIntegrativo: number;
    descrizione: string;
    note: string;
}

export interface DettagliQuadroRsModel {
    importoPresenteNelleFattureRicevute: number;
    importoNonPresenteNelleFattureRicevute: number;
    rigoRsId: string;
}

export interface DichiarazioneModelUnicoData {
    aliquotaImpostaSostitutiva: number;
    annoUnico: number;
    comune: string;
    frontespizioData: FrontespizioData;
    progressivoModulo: number;
    quadriRsData: QuadroRsData[];
    quadriRuData: QuadroRuData[];
    quadroLmData: QuadroLmData;
    quadroRnData: QuadroRnData;
    quadriIrpef: QuadroIrpef;
    speseEdOneri: OneriESpese;
    quadroRrSezione1Data: QuadroRrSezione1Data;
    quadroRrSezione2Data: QuadroRrSezione2Data;
    quadroRxData: QuadroRxData;
    statoDichiarazione: StatoDichiarazione;
    utente: UtenteUnicoData;
}

export interface UtenteUnicoData {
    capResidenza: string;
    civicoResidenza: string;
    codiceComuneNascita: string;
    codiceFiscale: string;
    codiceIstatResidenza: string;
    codiceProvinciaNascita: string;
    codiceProvinciaResidenza: string;
    comuneResidenza: string;
    comuneStatoEsteroNascita: string;
    dataFineIva: string | null;
    dataInizioIva: string;
    dataNascita: string;
    dugresidenza: string;
    indirizzo: string;
    indirizzoEmail: string;
    indirizzoResidenza: string;
    numeroTelefono: string;
    partitaIva: string;
    sesso: string;
    startUpNel: number;
}

export interface QuadroIrpef {
    hasQuadriAltriRedditi: boolean;
    hasAltriRedditiQuadriNonGestiti: boolean;
    redditiDaTerreni: RedditiDaTerreni[];
    redditiDaFabbricati: RedditiDaFabbricati[];
    redditiDaDipendente: RedditiDaDipendente[];
    familiariACarico: FamiliariACarico[];
    addizionaliComunaleRegionale: AddizionaliComunaleRegionale;
    cedolareSecca: CedolareSecca;
    scelta258PerMille: Scelta258PerMille;
    redditiTassazioneSeparata: RedditiTassazioneSeparata;
    redditiFinanziariEsteri: RedditiFinanziariEsteri[];
}

export interface CedolareSecca extends QuadroDichiarazione {
    totaleImpostaCedolareSecca: number;
    impostaSuRedditiDiversi21: number;
    totaleImpostaComplessiva: number;
    ritenuteCUlocazioniBrevi: number;
    differenza: number;
    eccedenzaDichiarazionePrecedente: number;
    eccedenzaCompensataModF24: number;
    accontiVersati: number;
    accontiSospesi: number;
    cedolareSeccaRisultanteDa730TrattenutaDalSostituto: number;
    cedolareSeccaRisultanteDa730RimborsataDalSostituto: number;
    impostaADebito: number;
    impostaACredito: number;
    primoAcconto: number;
    secondoOUnicoAcconto: number;
}

export interface Scelta258PerMille extends QuadroDichiarazione {
    sceltaStato: SceltaStato;
    scelta8PerMille: Scelta8PerMille;
    anomalia8PerMille: Anomalia8PerMille;
    scelta5PerMille: Scelta5PerMille;
    codiceFiscale5PerMille: string;
    partitoPolitico: PartitiPolitici;
}

export enum SceltaStato {
    FameNelMondo = 1,
    Calamita = 2,
    EdiliziaScolastica = 3,
    AssistenzaAiRifugiati = 4,
    BeniCulturali = 5,
    RecuperoTossicodipendenze = 6,
}

export enum Scelta8PerMille {
    Stato,
    ChiesaCattolica,
    UnioneChieseCristianeAvventisteDel7Giorno,
    AssembleeDiDioInItalia,
    ChiesaEvangelisticaValdese,
    ChiesaEvangelicaLuteranaInItalia,
    UnioneComunitaEbraicheItaliane,
    SacraArcidiocesiOrtodossaDItaliaEdEsarcatoPerLEuropaMeridionale,
    ChiesaApostolicaInItalia,
    UnioneCristianaEvangelicaBattistaDItalia,
    UnioneBuddhistaItaliana,
    UnioneInduistaItaliana,
    IstitutoBuddistaItalianoSokaGakkaiIBISG,
    AssociazioneChiesaDInghilterraInItalia,
    SceltaNonEffettuata,
}

export enum Scelta5PerMille {
    EntiDelTerzoSettoreIscrittiNelRUNTS,
    RicercaScientificaEUniversità,
    RicercaSanitaria,
    TutelaBeniCulturaliEPaesaggistici,
    AttivitàSocialiComuneDiResidenza,
    AssociazioniSportiveDilettantistiche,
    EntiGestoriAreeProtette,
}

export enum Anomalia8PerMille {
    NessunaAnomalia = 0,
    PresenzaPiuFirme = 1,
    UnicaFirmaSuPiuCaselle = 2,
    SegnoNonRiconducibileAFirma = 3,
    PresenzaCodiceStatoSenzaFirma = 4,
    PresenzaCodiceStatoErrato = 5,
    PresenzaDiPiuAnomalie,
}

export interface PartitiPolitici {
    id: number;
    displayName: string;
    code: string;
}

export interface QuadroRxData {
    impostaCompensazione: number | null;
    impostaSostitutivaCredito: number | null;
    impostaSostitutivaDebito: number | null;
    codiceFiscale: string;
    numeroModulo: string;
    irpefCredito: number | null;
    irpefDebito: number | null;
    addizionaleRegionaleADebito: number | null;
    addizionaleRegionaleACredito: number | null;
    addizionaleComunaleADebito: number | null;
    addizionaleComunaleACredito: number | null;
    impostaCriptoAttivitaADebito: number | null;
    impostaCriptoAttivitaACredito: number | null;
    impostaADebitoCredito: number | null;
}

export interface QuadroRuData {
    ammontareEccedente: string;
    annoEccedente: string;
    codiceCredito: string;
    codiceCreditoEccedente: string;
    codiceFiscale: string;
    creditoSpettante: string;
    creditoUsato: string;
    descrizioneCredito: string;
    numeroModulo: string;
    residuo: string;
    residuoCreditoPrecUNICO: string;
}

export interface QuadroRsData {
    artiProfessioniCodiceFiscale1: string;
    artiProfessioniCodiceFiscale2: string;
    artiProfessioniCodiceFiscale3: string;
    artiProfessioniReddito1: string;
    artiProfessioniReddito2: string;
    artiProfessioniReddito3: string;
    quadroDiRiferimento: string;
    riduzioneAccertamento: boolean;
    rsConsumi: string;
    anno: string;
    articolo: string;
    codiceAiuto: string;
    codiceAteco: string;
    codiceFiscale: string;
    codiceQuadro: string;
    conservazioneDocumenti: string;
    dimensioneImpresa: string;
    formaGiuridica: string;
    importoAiuto: string;
    numero: string;
    numeroModulo: string;
    ritenuteSubite: string;
    rsAffitti: string;
    rsBenzina: string;
    rsMerce: string;
    settore: string;
    tipoNorma: string;
    tipogiaCosti: string;
}

export interface QuadroRrSezione1Data {
    accontiContributiArtCom: string;
    alMeseArtCom: string;
    attivitàParticolari: string;
    codiceAziendaINPS: string;
    codiceFiscale2: string;
    codiceFiscale: string;
    codiceINPS: string;
    contributiFissiArtCom: string;
    contributoCreditoArtCom: string;
    contributoDebitoArtCom: string;
    contributoMartenitàArtCom: string;
    contributoSuEccedenteMinimaleArtCom: string;
    creditoArtComAnnoPrecedente: string;
    creditoArtComAnnoPrecedenteCompensatoF24: string;
    creditoArtComAnnoPrecedenteResiduo: string;
    creditoArtComDaCompensare: string;
    dalMeseArtCom: string;
    numeroModulo: string;
    redditoArtCom: string;
    redditoEccedenteMinimaleArtCom: string;
    redditoMinimaleArtCom: string;
    riduzioneDalMeseArtCom: string;
    riduzionealMeseArtCom: string;
    tipoRiduzioneArtCom: string;
    tipoRiduzioneSoggettoRR?: any;
    tipologiaIscritto: string;
    totaleVersatoFissiArtCom: string;
}

export interface QuadroRrSezione2Data {
    codiceReddito: string;
    creditoARimborso: string;
    creditoCompensato: string;
    esonero: boolean;
    importoEsonero: string;
    reddito: string;
    accontoVersato: string;
    alMese: string;
    aliquota: string;
    codiceFiscale: string;
    contributiDovuti: string;
    contributoACredito: string;
    contributoADebito: string;
    contributoDovuto: string;
    creditoDaUtilizzareInCompensazione: string;
    dalMese: string;
    imponibile: string;
    numeroModulo: string;
    reddito01: string;
}

export interface QuadroLmData {
    accontiVersati: number;
    assenzaCauseOstative: boolean;
    autonomo: boolean;
    codiceAttivita01: string;
    codiceAttivita02: string;
    codiceAttivita03: string;
    codiceAttivita04: string;
    codiceAttivita05: string;
    codiceAttivita06: string;
    codiceFiscale: string;
    contributiArticolo10: string;
    contributiFondoPerduto: string;
    contributiVersati: string;
    contributiVersatiUtilizzati: string;
    creditoPrecedenteDichiarazione: number;
    creditoPrecedenteDichiarazioneCompensato: number;
    differenza: number;
    eccedenzaContributi: string;
    impostaACredito: string;
    impostaADebito: string;
    impostaSostitutiva: string;
    impostaSostitutivaConcordatoPreventivoBiennale: number;
    imposteMenoRitenute: string; // da verificare se viene usato
    impresa: boolean;
    impresaFamiliare: boolean;
    incassi01: string;
    incassi02: string;
    incassi03: string;
    incassi04: string;
    incassi05: string;
    incassi06: string;
    numeroModulo: string;
    nuovaAttivita: boolean;
    nuovaAttività: boolean;
    percentualeReddito01: string;
    percentualeReddito02: string;
    percentualeReddito03: string;
    percentualeReddito04: string;
    percentualeReddito05: string;
    percentualeReddito06: string;
    redditiArtigianiCommercianti: string;
    redditiGestioneSeparata: string;
    reddito01: string;
    reddito02: string;
    reddito03: string;
    reddito04: string;
    reddito05: string;
    reddito06: string;
    redditoImponibile: string;
    redditoImponibileNetto: string;
    requisitoAccessoRegime: boolean;
    totaleReddito: string;
    totaleRitenute: string;
    totaleRitenuteConsorzio: string;
}

export interface FrontespizioData extends QuadroDichiarazione {
    checkQuadroLM: boolean;
    checkQuadroRN: boolean;
    checkQuadroRR: boolean;
    checkQuadroRS: boolean;
    checkQuadroRU: boolean;
    checkQuadroRX: boolean;
    codiceComuneResidenza2019: string;
    codiceComuneResidenza: string;
    codiceFiscale2: string;
    codiceFiscale3: string;
    codiceFiscale: string;
    cognome: string;
    comuneDiNascita: string;
    comuneResidenza2019: string;
    comuneResidenza: string;
    indirizzoResidenza: string;
    civicoResidenza: string;
    capResidenza: string;
    dataDiNascita: string;
    dataImpegnoPresentazioneTelematica: string;
    firmaDelContribuente: string;
    nome: string;
    sessoF: boolean;
    sessoM: boolean;
    siglaProvinciaDiNascita: string;
    siglaProvinciaResidenza2019: string;
    siglaProvinciaResidenza: string;
    soggettoChePredisponeLaDichiarazione: string;
}

export class DataToSendDichiarazione {
    forfettarioService: ForfettarioService = ServiceLocator.injector.get(ForfettarioService);

    constructor(
        public forfettarioId: number,
        public forfettarioCodiceFiscale: string,
        public anno: number,
        public dataInizioIva?: string,
        public startUpNel?: number,
        public dataFineIva?: string,
    ) {
        this.loadForfettarioBaseInformation();
    }

    async loadForfettarioBaseInformation() {
        const tokenContainerUtente$ = this.forfettarioService.getTokenContainerUtente(this.forfettarioId, { cacheData: true });
        const getAccountData$ = this.forfettarioService.getAccountData(this.forfettarioId, { cacheData: true });

        await forkJoin({ tokenContainerUtente$, getAccountData$ }).toPromise();
    }
}

export interface DichiarazioneDeiRedditiData {
    dataToSendDichiarazione: DataToSendDichiarazione;
    clientOptions: ForfettappApiClientOptions;
}

export interface Dashboard {
    id: number;
    dichiarazioneId: number;
    limiteRicavi: number;
    totaleRicavi: number;
    impostaSostitutiva: number;
    reddito: number;
    statoDichiarazione: number;
    scadenze: Scadenza[];
    aliquotaContributiPrevidenziali: number;
    descrizioneContributoPrevidenzialeInFattura: string;
}

export interface TotalePerFornitore {
    codiceFiscaleFornitore: string;
    totale: number;
}

export const descrizioniStatoDichiarazione: { [stato: number]: string } = {
    [1]: 'Aperta',
    [2]: 'Provvisoria',
    [3]: 'Chiusa',
};

export interface DatiPrecompilata {
    datiSintetici: DatiSintetici;
    datiSpeseSanitarie: DatiSpeseSanitarie;
    anno: number;
    dataElaborazione: Date;
}

export interface DatiSintetici {
    sintetico730: Sintetico730;
    eredita: boolean;
    anno: number;
    codFiscIn: string;
}

export interface Sintetico730 {
    datiAnagrafici: DatiAnagrafici;
    dataElaborazione: string;
    elencoFonti: ElencoFonti;
}

export interface ElencoFonti {
    fonte: Fonte[];
}

export interface Fonte {
    tipoFonte: string;
    verifica: number;
    dettaglioFonte: DettaglioFonte[];
}

export interface DettaglioFonte {
    testata: string;
    testataImporto: string;
    testataUtilizzo: string;
    dettaglio: Dettaglio[];
}

export interface Dettaglio {
    contenuto: string;
    importo: string;
    utilizzo: string;
}

export interface DatiAnagrafici {
    anagrafica: Anagrafica;
}

export interface Anagrafica {
    codiceFiscale: string;
    nome: string;
    cognome: string;
}

export interface DettaglioSpesa {
    voceSpesa: VoceSpesa[];
}

export interface DettaglioSpese {
    esito: boolean;
    listaMessaggi: ListaMessaggi;
    listaDati730: ListaDati730;
    pagina: number;
    totalePagine: number;
}

export interface DocumentoSpesa {
    dataEmissione: string;
    dataPagamento: string;
    numDocumentoFiscale: string;
    emessoDa: string;
    denominazione: string;
    importoTotale: number;
    rimborso: string;
    pagamentoTracciato: string;
    dettaglioSpesa: DettaglioSpesa;
    pIva: string;
}

export interface ListaDati730 {
    documentoSpesa: DocumentoSpesa[];
}

export interface ListaMessaggi {}

export interface DatiSpeseSanitarie {
    dettaglioSpese: DettaglioSpese[];
    cfSpese: string;
    messaggio: string;
    anno: number;
    codFiscIn: string;
}

export interface VoceSpesa {
    tipoSpesa: string;
    importo: number;
}

export interface Immobile {
    catasto: string;
    titolarita: string;
    ubicazione: string;
    foglio: string;
    particella: string;
    subalterno: string;
    categoria: string;
    classe: string;
    consistenza: string;
    rendita: string;
}

export enum StatoDichiarazione {
    Aperta = 1,
    Provvisoria = 2,
    Chiusa = 3,
}

export enum TipoAgenteEnasarco {
    NonIscritto = 0,
    MonoMandatario = 1,
    PluriMandatario = 2,
}

export const TipoAgenteEnasarcoDescriptions: Record<TipoAgenteEnasarco, string> = {
    [TipoAgenteEnasarco.NonIscritto]: 'Non iscritto Enasarco',
    [TipoAgenteEnasarco.MonoMandatario]: 'MonoMandatario',
    [TipoAgenteEnasarco.PluriMandatario]: 'PluriMandatario',
};

export enum TipoContribuzione {
    NonGestita = 0,
    GestioneArtigiani = 1,
    GestioneCommercianti = 2,
    GestioneArtigianiRidForfettari = 3,
    GestioneCommerciantiRidForfettari = 4,
    GestioneArtigianiOver65Anni = 5,
    GestioneCommerciantiOver66Anni = 6,
    GestioneSeparataINPS = 7,
    GestioneSeparataINPSConAltraContribuzione = 8,
    CassaGeometri = 9,
    AffittacamereCommercianti = 10,
    CassaPsicologi = 11,
    CassaMediciENPAM = 12,
    INARCASSA = 13,
    IstitutoNazionalePrevidenzaGiornalistiItalianiIscrittiMeno5Anni = 14,
    AffittacamereRiduzioneForfettari = 15,
    ENPAPI = 16,
    CassaAvvocati = 17,
    Enpab = 18, // Enpab - Previdenza assistenza biologi
    NoContribuzioneGiaDipendente = 19,
    Eppi = 20,
    Epap = 21,
    EnteNazionalePrevidenzaAssistenzaVeterinariENPAV = 22,
    EnteNazionalePrevidenzaAssistenzaImpiegatiAgricolturaENPAIA = 23,
    GestioneArtigianiRid50Percento = 26,
    GestioneCommerciantiRid50Percento = 27,
}

export const descrizioniContribuzioni: Array<{ value: TipoContribuzione; description: string }> = [
    {
        value: TipoContribuzione.NonGestita,
        description: 'Non gestita',
    },
    {
        value: TipoContribuzione.GestioneArtigiani,
        description: 'Gestione artigiani',
    },
    {
        value: TipoContribuzione.GestioneCommercianti,
        description: 'Gestione commercianti',
    },
    {
        value: TipoContribuzione.GestioneArtigianiRidForfettari,
        description: 'Gestione artigiani rid. forfettari',
    },
    {
        value: TipoContribuzione.GestioneCommerciantiRidForfettari,
        description: 'Gestione commercianti rid. forfettari',
    },
    {
        value: TipoContribuzione.GestioneArtigianiOver65Anni,
        description: 'Gestione artigiani over 65 anni',
    },
    {
        value: TipoContribuzione.GestioneCommerciantiOver66Anni,
        description: 'Gestione commercianti over 66 anni',
    },
    {
        value: TipoContribuzione.GestioneSeparataINPS,
        description: 'Gestione separata INPS',
    },
    {
        value: TipoContribuzione.GestioneSeparataINPSConAltraContribuzione,
        description: 'Gestione separata INPS con altra contribuzione',
    },
    {
        value: TipoContribuzione.CassaGeometri,
        description: 'Cassa Geometri',
    },
    {
        value: TipoContribuzione.AffittacamereCommercianti,
        description: 'Affittacamere/agenti ass (commercianti)',
    },
    {
        value: TipoContribuzione.CassaPsicologi,
        description: 'Cassa Psicologi',
    },
    {
        value: TipoContribuzione.CassaMediciENPAM,
        description: 'Cassa medici (ENPAM)',
    },
    {
        value: TipoContribuzione.INARCASSA,
        description: 'INARCASSA',
    },
    {
        value: TipoContribuzione.IstitutoNazionalePrevidenzaGiornalistiItalianiIscrittiMeno5Anni,
        description: 'Istituto Nazionale Previdenza Giornalisti Italiani (iscritti meno 5 anni)',
    },
    {
        value: TipoContribuzione.AffittacamereRiduzioneForfettari,
        description: 'Affittacamere/agenti ass. (riduzione forfettari)',
    },
    {
        value: TipoContribuzione.ENPAPI,
        description: 'ENPAPI- Ente Nazionale Previdenza e Assistenza Professione Infermieristica',
    },
    {
        value: TipoContribuzione.CassaAvvocati,
        description: 'Cassa Nazionale Previdenza e Assistenza Avvocati e Procuratori Legali',
    },
    {
        value: TipoContribuzione.Enpab,
        description: 'Enpab - Previdenza assistenza biologi',
    },
    {
        value: TipoContribuzione.NoContribuzioneGiaDipendente,
        description: 'No contribuzione art/com  (già dipendente con attività prevalente)',
    },
    {
        value: TipoContribuzione.Eppi,
        description: 'EPPI - Cassa Previdenziale Periti Industriali e periti industriali laureati',
    },
    {
        value: TipoContribuzione.Epap,
        description: 'Ente previdenza e assistenza pluricategoriale (EPAP)',
    },
    {
        value: TipoContribuzione.EnteNazionalePrevidenzaAssistenzaVeterinariENPAV,
        description: 'ENPAV - Ente Nazionale Previdenza e Assistenza Veterinari',
    },
    {
        value: TipoContribuzione.EnteNazionalePrevidenzaAssistenzaImpiegatiAgricolturaENPAIA,
        description: 'ENPAIA - Ente Nazionale Previdenza e Assistenza Impiegati Agricoltura',
    },
    {
        value: TipoContribuzione.GestioneArtigianiRid50Percento,
        description: 'Gestione artigiani riduzione 50%',
    },
    {
        value: TipoContribuzione.GestioneCommerciantiRid50Percento,
        description: 'Gestione commercianti riduzione 50%',
    },
];

export interface RitenutaCertificataMensile {
    mese: number;
    importo: number;
    documentoId?: string; // solo voci già salvate
    pdfUrl?: string;      // solo voci già salvate
}

export interface RitenuteCertificateDetail {
    ritenuteCertificate: number;
    voci: RitenutaCertificataMensile[];
}
